import React, { ReactNode, useCallback } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import qs from 'qs';

import { ComponentLinkProxy } from 'src/components/component-link';
import { useIntl } from 'src/hooks/intl';
import { useLocation, useParams } from 'src/router/hooks';
import urls from 'src/urls/source';

import { TreeEntry } from '../../types';
import { getMatchParts } from '../../utils/filter';
import { getSourceItemIcon } from '../../utils/source-icons';
import DirectoryListRow from '../directory-row';
import * as styles from '../directory.style';
import SubmoduleLink from '../submodule-link';

type ItemLinkProps = {
  item: TreeEntry;
  renderName: (name: string) => ReactNode;
  icon?: ReactNode;
};

type Props = {
  file: TreeEntry;
  query: string | null | undefined;
};

const Spacer = styled.span`
  margin: 0 4px;
`;

/* eslint @typescript-eslint/ban-types: "warn" */
const ItemLink: React.FC<ItemLinkProps> = ({ renderName, item, icon }) => {
  const location = useLocation();
  const { refOrHash, repositoryOwner, repositorySlug } = useParams<{
    refOrHash?: string;
    repositoryOwner: string;
    repositorySlug: string;
  }>();
  const { at } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const url = urls.ui.source(`${repositoryOwner}/${repositorySlug}`, {
    path: item.name,
    refOrHash,
    at: at as string | undefined,
  });

  const renderLinkContent = (linkIcon: ReactNode, text: ReactNode) => (
    <styles.LinkInnerWrapper>
      {linkIcon && (
        <styles.LinkIconContainer>{linkIcon}</styles.LinkIconContainer>
      )}
      <styles.LinkTextContainer>{text}</styles.LinkTextContainer>
    </styles.LinkInnerWrapper>
  );

  if (item.type === 'file' || item.type === 'symlink') {
    return (
      <ComponentLinkProxy href={url}>
        {renderLinkContent(icon, renderName(item.name))}
      </ComponentLinkProxy>
    );
  }

  if (item.type === 'directory') {
    return (
      <ComponentLinkProxy href={url}>
        {renderLinkContent(icon, renderName(item.name))}
      </ComponentLinkProxy>
    );
  }

  if (item.type === 'submodule') {
    return <SubmoduleLink entry={item} icon={icon} renderName={renderName} />;
  }

  // This shouldn't be possible
  return null;
};

// @ts-ignore TODO: fix noImplicitAny error here
const withSeparatorSpacing = text => {
  const parts = text.split('/');
  return parts.slice(1, parts.length).reduce(
    // @ts-ignore TODO: fix noImplicitAny error here
    (acc, part, idx) => {
      acc.push(<Spacer key={idx}>/</Spacer>);
      acc.push(part);
      return acc;
    },
    [parts[0]]
  );
};

const FilteredItem = (props: Props) => {
  const { file, query } = props;
  const intl = useIntl();

  const renderName = useCallback(
    (name: string) => {
      const matchParts = getMatchParts(name, query);
      return matchParts.map((part, idx) => {
        const key = `${name}-${idx}`;
        const text = withSeparatorSpacing(part.text);
        if (part.match) {
          return <strong key={key}>{text}</strong>;
        }
        return <span key={key}>{text}</span>;
      });
    },
    [query]
  );

  return (
    <DirectoryListRow hideFileMetadata>
      <ItemLink
        item={file}
        icon={getSourceItemIcon(file.type, intl)}
        renderName={renderName}
      />
    </DirectoryListRow>
  );
};

export default FilteredItem;
